import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector, shallowEqual } from "react-redux"
import { DateTime } from "luxon"
import Button from "react-bootstrap/Button"
import Table from "react-bootstrap/Table"

import { Popup } from "components"
import { Select } from "components/Form"
import ESignatureModal from "components/Lims/ESignatureModal"
import StatusBadge from "components/Lims/StatusBadge"
import { usePhaseOverlay } from "./PhaseSection"
import {
  limsResults,
  limsConsumables,
  limsSampleQc,
  limsReruns,
  limsBatchHold,
  sampleView,
  assets,
  message,
} from "state_management"
import { QA_DECISION } from "state_management/lims/limsResults"
import { canSign } from "state_management/lims/segregationOfDuties"
import useT from "utils/hooks/useT"

// QaDecisionSection (SPEC-022 / K5) — the per-sample QA decisions ON the analysis in the plan.
// Replaces the separate QaAnalysisReviewModal (deleted): ALL QC work — entry → review → QA
// decision — now happens in the "QC Results & Approvals" section of the analysis task, and the
// LIMS QA worklist deep-links here instead of opening a modal replica.
//
// The redux flow is the modal's, verbatim: every still-pending (sent-to-qa) result gets a
// per-sample decision — Approve / RERUN / STOP — and ONE "QA Reviewed" e-signature (capability
// "qa", 4-eyes excluding the analyst enterer(s) and the reviewer(s)) COMMITS them all:
//   - Approve -> result `qa-approved` (release-eligible)
//   - RERUN   -> result `qa-rerun`  + a new RERUN sample is spawned (limsReruns.createRerun)
//   - STOP    -> result `qa-stopped`+ the sample's batch/lot is rejected (limsBatchHold.hold)
// A clean Pass defaults to Approve; already-decided samples show their committed disposition
// read-only.
//
// VISIBILITY (GMP / ALCOA+, PRD-006 goal 2): the section is ALWAYS rendered — for everyone — as
// soon as the analysis has results in the QA stage, so the whole team can SEE where the analysis
// stands. The decision controls are GHOSTED (disabled + a short explanation) when the current
// user lacks the "qa" capability or is four-eyes-excluded (entered or reviewed these results);
// the ESignatureModal + server enforce the same gates authoritatively.
//
// Props: analysisId (the analysis run), analysisLabel (its display name, for the success message).
export default function QaDecisionSection({ analysisId, analysisLabel = "" }) {
  const { t } = useT("common")
  const dispatch = useDispatch()

  // Every result in the QA stage for this analysis (still-pending + already-decided).
  const results = useSelector(
    (state) => (analysisId ? limsResults.selectQaResultsForAnalysis(state, analysisId) : []),
    shallowEqual
  )
  const members = useSelector((state) => state.members)
  const assetsById = useSelector((state) => (state.asset && state.asset.assets) || {})
  const consumables = useSelector(limsConsumables.selectConsumables, shallowEqual)
  // QC details (batch/lot) per sample, needed to resolve the STOP batch-reject lot.
  const sampleQcMap = useSelector((state) => state.limsSampleQc, shallowEqual)
  // Every sample group from the Sample View (reliable sampleId -> sampleName / product /
  // observation + the group's analysis ids). Sample display + the RERUN clone resolve from here —
  // robust regardless of whether the planned-samples endpoint has this analysis loaded.
  const sampleGroups = useSelector((state) => state.sampleView.sampleGroups || [], shallowEqual)

  // QA's per-sample overrides, keyed by result id. A result not present here uses its DEFAULT
  // decision (a clean Pass pre-marks Approve; anything else is unset — QA must make an explicit
  // call). Derived rather than seeded into state, so there is no setState-in-effect.
  const [overrides, setOverrides] = useState({}) // { [resultId]: QA_DECISION | "" }
  const [showSign, setShowSign] = useState(false)
  const [saving, setSaving] = useState(false)
  // K13 (a) AMEND flow: the QA-decided result being corrected, the chosen new decision (≠ current),
  // and whether the mandatory-reason e-signature dialog is open. A two-step flow — pick the new
  // decision, then sign — so a typo correction is a deliberate, signed, reason-mandatory action.
  const [amending, setAmending] = useState(null) // the result record being amended, or null
  const [amendDecision, setAmendDecision] = useState("") // the chosen corrected QA_DECISION
  const [showAmendSign, setShowAmendSign] = useState(false)

  // #4106: this section lives inside the QA PhaseSection, which dims itself with `opacity`
  // whenever the work is not currently at the QA stage. That opacity applies to our modals
  // too and boxes them into the section's stacking context, so "Amend decision" opened as a
  // see-through card layered under the rest of the panel. Tell the phase to stop ghosting
  // while any of ours is open.
  usePhaseOverlay(showSign || !!amending || showAmendSign)

  const hasResults = results.length > 0

  // The lot/sample-group/asset reference data the section needs. Loaded only once the analysis
  // actually has QA-stage results (most analysis opens never reach this stage — no idle fetches).
  useEffect(() => {
    if (!hasResults) return
    dispatch(limsSampleQc.fetch())
    dispatch(sampleView.fetch())
    dispatch(assets.fetch())
  }, [dispatch, hasResults])

  const memberInitials = (id) => (id && members[id] && members[id].initials) || ""

  // Only the still-pending (sent-to-qa) results can be decided; already-decided rows are read-only
  // and reflect their committed disposition.
  const pending = useMemo(() => results.filter((r) => r.state === "sent-to-qa"), [results])

  // The effective decision shown/committed for one pending result: QA's explicit override if set,
  // else the default (clean effective Pass -> Approve; otherwise unset).
  const decisionFor = (r) => {
    if (overrides[r.id] !== undefined) return overrides[r.id]
    return limsResults.effectiveOverall(r) === "PASS" ? QA_DECISION.APPROVE : ""
  }

  // The reviewer(s) and analyst enterer(s) across the analysis's results — excluded from the QA
  // signature for 4-eyes (QA must be a third party).
  const excludedMemberIds = useMemo(() => {
    const set = new Set()
    results.forEach((r) => {
      if (r.enteredBy) set.add(r.enteredBy)
      if (r.reviewerSignature && r.reviewerSignature.memberId) set.add(r.reviewerSignature.memberId)
      if (r.reviewedBy) set.add(r.reviewedBy)
    })
    return Array.from(set)
  }, [results])

  // May the current user DECIDE here? (holds "qa" AND is not an enterer/reviewer of these
  // results). When not, the section stays fully visible but its controls are ghosted with a
  // short explanation (GMP: everyone can SEE, only the qualified can ACT).
  const qaGate = useSelector(
    (state) => canSign(state, { capability: "qa", excludedMemberIds }),
    shallowEqual
  )
  let ghostHint = null
  if (!qaGate.allowed) {
    const ghostKey = qaGate.reasonKey === "limsEsigBlockedSelf" ? "limsQaGhostFourEyes" : "limsQaGhostCapability"
    ghostHint = t(ghostKey)
  }

  // Analysis context from the (shared) result snapshot: instrument + consumables used on the run.
  const context = useMemo(() => {
    const first = results[0]
    if (!first) return { instrumentName: "", consumableNames: [] }
    const asset = first.instrumentId && assetsById[first.instrumentId]
    const instrumentName = asset ? asset.name || asset.displayName || asset.display_name || "" : ""
    const consumableNames = (first.reagentLotIds || [])
      .map((id) => {
        const c = consumables.find((x) => x.id === id)
        return c ? c.name : null
      })
      .filter(Boolean)
    return { instrumentName, consumableNames }
  }, [results, assetsById, consumables])

  // Sample display info (name / product / observation) by sample id, built from the Sample View
  // groups (the same source the worklist + Sample View use).
  const sampleInfo = useMemo(() => {
    const map = {}
    sampleGroups.forEach((g) => {
      ;(g.analyses || []).forEach((a) => {
        if (a.sampleId && !map[a.sampleId]) {
          map[a.sampleId] = {
            sampleName: g.sampleName,
            observationId: g.observationId,
            productName: g.productName,
          }
        }
      })
    })
    return map
  }, [sampleGroups])

  const setDecision = (resultId, value) =>
    setOverrides((prev) => ({ ...prev, [resultId]: value }))

  const decisionOptions = [
    { id: QA_DECISION.APPROVE, name: t("limsQaReviewApprove") },
    { id: QA_DECISION.RERUN, name: t("limsQaReviewRerun") },
    { id: QA_DECISION.STOP, name: t("limsQaReviewStop") },
  ]

  // Every pending result must carry a decision (default or override) before "QA Reviewed" can sign.
  const allDecided = pending.length > 0 && pending.every((r) => decisionFor(r))

  // The batch/lot for a result's sample (the F-C1 QC detail), used for the STOP batch-reject.
  const batchLotFor = (r) => {
    const qc = r.sampleId ? sampleQcMap[r.sampleId] : null
    return (qc && qc.batchLot) || ""
  }

  // SPEC-020 (C3): a STOP decision REQUIRES a batch/lot registered on the sample's QC details —
  // without one the hold cannot be placed, yielding a `qa-stopped` result WITHOUT the
  // BATCH_REJECTED release block (a stop that stops nothing). Such decisions BLOCK the sign-off
  // until QA registers the lot (or changes the decision); the message below names the samples.
  const stopsMissingLot = pending.filter((r) => decisionFor(r) === QA_DECISION.STOP && !batchLotFor(r))
  const stopMissingLotNames = stopsMissingLot
    .map((r) => (sampleInfo[r.sampleId] && sampleInfo[r.sampleId].sampleName) || r.sampleId || "—")
    .join(", ")
  // SPEC-048 R6 (C1): a FAIL result without a signed override may NOT be QA-approved — the backend
  // refuses it (422) so out-of-spec material can never be released on an unsigned deviation.
  // `effectiveOverall` already folds the override in, so `=== "FAIL"` means "failing AND not
  // overridden". Such an APPROVE pick blocks the sign-off (the signer must rerun/stop it, or a
  // reviewer/supervisor must first sign an OK-override on the result); the message names the samples.
  const approveFailNoOverride = pending.filter(
    (r) => decisionFor(r) === QA_DECISION.APPROVE && limsResults.effectiveOverall(r) === "FAIL"
  )
  const approveFailNames = approveFailNoOverride
    .map((r) => (sampleInfo[r.sampleId] && sampleInfo[r.sampleId].sampleName) || r.sampleId || "—")
    .join(", ")
  // The sign-button hint, in priority order: the ghost reason, the STOP-without-lot block, the
  // approve-FAIL-without-override block, then "decide everything".
  let signHint
  if (!qaGate.allowed) {
    signHint = ghostHint
  } else if (stopsMissingLot.length > 0) {
    signHint = t("limsQaReviewStopNoLot", { samples: stopMissingLotNames })
  } else if (approveFailNoOverride.length > 0) {
    signHint = t("limsQaReviewApproveFailNoOverride", { samples: approveFailNames })
  } else if (!allDecided) {
    signHint = t("limsQaReviewDecideAllHint")
  }

  // Resolve the original sample (id + number + product + analysis ids) for a RERUN. Falls back to
  // the loaded sample info / the result's own sampleId when the samples slice has no richer data.
  // SPEC-054: the server derives the rerun sample from the original, so only its identity is
  // needed. The old `analysisIds` fallback sent the analysis RUN id into a lookup that only accepts
  // TEMPLATE ids, which is why no rerun sample was ever created (Plannertech-user#4078).
  const resolveOriginalSample = (r) => {
    const info = sampleInfo[r.sampleId] || {}
    return {
      id: r.sampleId,
      sampleName: info.sampleName || "",
      productName: info.productName || null,
    }
  }

  // Apply ONE committed QA decision's side effect (after its result state is set): RERUN spawns a
  // new sample, STOP rejects the batch/lot. Approve has none. A side-effect failure must NOT undo
  // the already-signed decision, so we swallow.
  const applySideEffect = (r, decision, signature) => {
    if (decision === QA_DECISION.RERUN) {
      return dispatch(
        limsReruns.createRerun({
          originalSample: resolveOriginalSample(r),
          analysisId,
          hasApproval: true, // the QA "QA Reviewed" signature IS the approval
          qaDecisionId: null,
        })
      ).catch(() => {
        // Never swallow this. QA has signed that the lot MAY be re-analysed; if the rerun sample
        // does not appear, the lab is waiting for work that does not exist. A silent catch here is
        // precisely why Plannertech-user#4078 went unnoticed.
        dispatch(message.error(t("limsRerunCreateFailed")))
        return undefined
      })
    }
    if (decision === QA_DECISION.STOP) {
      const lot = batchLotFor(r)
      // SPEC-020: a STOP without a lot is BLOCKED before signing (stopsMissingLot gates the
      // button), so this branch is unreachable via the UI — kept as a defensive no-op only.
      if (!lot) return Promise.resolve(undefined)
      return dispatch(limsBatchHold.hold(lot, { signature })).catch(() => undefined)
    }
    return Promise.resolve(undefined)
  }

  // Commit every per-sample decision under ONE QA signature: PATCH each result state (the
  // server verifies the QA password + capability + 4-eyes and stamps qaSignature), then apply
  // each decision's FE-created side effect (B-D5: rerun sample / batch hold). Done sequentially
  // per result so a rerun's sample-create and the batch holds resolve before the success
  // message. Returns the promise so the e-signature dialog shows a backend 403 inline.
  const handleSign = (signature) => {
    // SPEC-020 defense in depth: the button is gated on stopsMissingLot, but never commit a
    // signed STOP whose batch hold cannot be placed.
    if (stopsMissingLot.length > 0) {
      dispatch(message.error(t("limsQaReviewStopNoLot", { samples: stopMissingLotNames })))
      return undefined
    }
    // SPEC-048 (R6/C1) defense in depth: never commit an APPROVE on a FAIL-without-override result
    // (the button is gated on it too); the backend would 422 the transition.
    if (approveFailNoOverride.length > 0) {
      dispatch(message.error(t("limsQaReviewApproveFailNoOverride", { samples: approveFailNames })))
      return undefined
    }
    setSaving(true)
    const commit = pending.reduce((chain, r) => {
      const decision = decisionFor(r)
      return chain
        .then(() => dispatch(limsResults.setQaDecision(r.id, decision, signature)))
        .then(() => applySideEffect(r, decision, signature))
    }, Promise.resolve())
    return commit
      .then(() => {
        setShowSign(false)
        dispatch(message.success(t("limsQaReviewedDone", { analysis: analysisLabel })))
      })
      .finally(() => setSaving(false))
  }

  // K13 (a): open the AMEND flow for a QA-decided result — reset the chosen decision and step to
  // the "pick a new decision" stage. Ghosted controls never reach here (the affordance is disabled).
  const startAmend = (r) => {
    setAmending(r)
    setAmendDecision("")
    setShowAmendSign(false)
  }
  const cancelAmend = () => {
    setAmending(null)
    setAmendDecision("")
    setShowAmendSign(false)
  }

  // K13 (a): commit the amendment under a NEW QA signature with a MANDATORY reason. The thunk
  // returns the FULL response envelope so the conditional batch-hold side effects (under the SAME
  // signature) are surfaced: a TO-stopped amend auto-holds the lot (or warns none could be held);
  // an AWAY-from-stopped amend leaves any active hold in place (warns it must be lifted separately).
  // Returns the promise so the e-signature dialog shows a backend 403/409/422 inline.
  const handleAmendSign = (signature) => {
    if (!amending || !amendDecision) return undefined
    setSaving(true)
    return dispatch(limsResults.amendQaDecision(amending.id, amendDecision, signature))
      .then((res) => {
        const autoHold = res && res.autoHold
        const existingHold = res && res.existingHold
        if (autoHold && autoHold.created) {
          dispatch(message.success(t("limsAmendHoldCreated", { lot: autoHold.batchLot })))
        } else if (autoHold && !autoHold.created) {
          dispatch(message.warning(t("limsAmendHoldNoLot")))
        }
        if (existingHold && existingHold.batchLot) {
          dispatch(message.warning(t("limsAmendHoldRemains", { lot: existingHold.batchLot })))
        }
        cancelAmend()
        dispatch(message.success(t("limsAmendDone", { analysis: analysisLabel })))
      })
      .finally(() => setSaving(false))
  }

  // Nothing in the QA stage yet — the analysis has no sent-to-qa / QA-decided results, so there
  // is no QA work (or history) to show.
  if (!hasResults) return null

  const controlsDisabled = saving || !qaGate.allowed

  return (
    <div className="ResultEntryPanel-qa">
      <div className="ResultEntryPanel-qa-head">
        <h5 className="ResultEntryPanel-qa-title">{t("limsQaSectionTitle")}</h5>
        {/* The run context the old QA modal surfaced (instrument + consumables, from the result
            snapshot) — kept in the section header so QA sees it next to the decisions. */}
        <div className="ResultEntryPanel-qa-context">
          <span className="ResultEntryPanel-qa-context-item">
            <span className="ResultEntryPanel-qa-context-label">{t("limsQaReviewInstrument")}</span>
            {context.instrumentName || t("none")}
          </span>
          <span className="ResultEntryPanel-qa-context-item">
            <span className="ResultEntryPanel-qa-context-label">{t("limsQaReviewConsumables")}</span>
            {context.consumableNames.length ? context.consumableNames.join(", ") : t("none")}
          </span>
        </div>
      </div>

      {/* Ghosted-not-hidden (GMP): the section is visible to everyone; a viewer who cannot decide
          sees WHY in one line. */}
      {!qaGate.allowed && <p className="ResultEntryPanel-qa-ghost">{ghostHint}</p>}

      <Table responsive className="ResultEntryPanel-qa-table">
        <thead>
          <tr>
            <th>{t("limsResultSample")}</th>
            <th>{t("limsResultSampleId")}</th>
            <th>{t("limsResultProduct")}</th>
            <th>{t("limsResultJudgement")}</th>
            <th>{t("limsQaReviewReviewer")}</th>
            <th>{t("limsQaReviewDecision")}</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => {
            const info = sampleInfo[r.sampleId] || {}
            const effective = limsResults.effectiveOverall(r)
            const overridden = !!(r.okOverride && r.overall === "FAIL")
            const decided = r.state !== "sent-to-qa"
            const reviewerId = (r.reviewerSignature && r.reviewerSignature.memberId) || r.reviewedBy
            const reviewerInitials = memberInitials(reviewerId) || "—"
            return (
              <tr key={r.id}>
                <td>{info.sampleName || "—"}</td>
                <td>{info.observationId || "—"}</td>
                <td>{info.productName || t("none")}</td>
                <td>
                  <JudgementBadge effective={effective} t={t} />
                  {overridden && (
                    <span className="ResultEntryPanel-qa-override"> ({t("limsResultOverrideShort")})</span>
                  )}
                  {r.oot && (
                    <div className="ResultEntryPanel-qa-oot" title={r.ootComment || ""}>
                      {t("limsResultOot")}
                      {r.ootComment ? `: ${r.ootComment}` : ""}
                    </div>
                  )}
                </td>
                <td>{reviewerInitials}</td>
                <td className="ResultEntryPanel-qa-decision">
                  {decided ? (
                    <div className="ResultEntryPanel-qa-decided">
                      <StatusBadge variant={decidedVariant(r.state)}>{t(decidedLabelKey(r.state))}</StatusBadge>
                      {/* K13 (a): the decision CHAIN — every superseded decision, oldest first, so a
                          correction's audit trail reads in one place (the original is never lost). */}
                      <DecisionChain history={r.decisionHistory} memberInitials={memberInitials} t={t} />
                      {/* GMP: everyone SEES the amend affordance; only QA (4-eyes) can act on it. */}
                      <Button
                        variant="link"
                        size="sm"
                        className="ResultEntryPanel-qa-amend"
                        onClick={() => startAmend(r)}
                        disabled={controlsDisabled}
                        title={ghostHint || undefined}
                      >
                        {t("limsAmendAction")}
                      </Button>
                    </div>
                  ) : (
                    <Select
                      isMulti={false}
                      includeNone
                      noneLabel={t("limsQaReviewChoose")}
                      collection={decisionOptions}
                      value={decisionFor(r) || ""}
                      onChange={(v) => setDecision(r.id, firstOf(v) || "")}
                      menuPlacement="auto"
                      disabled={controlsDisabled}
                    />
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </Table>

      {/* SPEC-020 (C3): STOP decisions without a registered batch/lot block the sign-off. */}
      {qaGate.allowed && stopsMissingLot.length > 0 && (
        <p className="ResultEntryPanel-qa-stop-no-lot">
          {t("limsQaReviewStopNoLot", { samples: stopMissingLotNames })}
        </p>
      )}

      {/* SPEC-048 (R6/C1): an APPROVE pick on a FAIL result without a signed override blocks the
          sign-off — the backend refuses it (422). */}
      {qaGate.allowed && approveFailNoOverride.length > 0 && (
        <p className="ResultEntryPanel-qa-stop-no-lot">
          {t("limsQaReviewApproveFailNoOverride", { samples: approveFailNames })}
        </p>
      )}

      {pending.length > 0 && (
        <div className="ResultEntryPanel-qa-actions">
          <Button
            variant="primary"
            onClick={() => setShowSign(true)}
            disabled={controlsDisabled || !allDecided || stopsMissingLot.length > 0 || approveFailNoOverride.length > 0}
            title={signHint}
          >
            {t("limsQaReviewedAction")}
          </Button>
        </div>
      )}

      {showSign && (
        <ESignatureModal
          title={t("limsQaReviewedAction")}
          requiredCapability="qa"
          excludedMemberIds={excludedMemberIds}
          onSign={handleSign}
          onCancel={() => setShowSign(false)}
        />
      )}

      {/* K13 (a) AMEND — step 1: pick the corrected decision (the current one is excluded so a
          correction always CHANGES the disposition). */}
      {amending && !showAmendSign && (
        <Popup nonScrollable title={t("limsAmendTitle")} onCancel={cancelAmend}>
          <div className="ResultEntryPanel-qa-amend-pick">
            <p className="ResultEntryPanel-qa-amend-current">
              {t("limsAmendCurrent")} <StatusBadge variant={decidedVariant(amending.state)}>{t(decidedLabelKey(amending.state))}</StatusBadge>
            </p>
            <label className="ResultEntryPanel-qa-amend-label">{t("limsAmendNewDecision")}</label>
            <Select
              isMulti={false}
              includeNone
              noneLabel={t("limsQaReviewChoose")}
              collection={decisionOptions.filter((o) => o.id !== amending.state)}
              value={amendDecision || ""}
              onChange={(v) => setAmendDecision(firstOf(v) || "")}
              menuPlacement="auto"
            />
            <p className="ResultEntryPanel-qa-amend-note">{t("limsAmendNote")}</p>
            {/* SPEC-048 (R6/C1): an amend to APPROVE on a FAIL-without-override result is refused by
                the backend (422) exactly like a first-time approval — block it here too. */}
            {amendDecision === QA_DECISION.APPROVE && limsResults.effectiveOverall(amending) === "FAIL" && (
              <p className="ResultEntryPanel-qa-stop-no-lot">{t("limsQaAmendApproveFailNoOverride")}</p>
            )}
            <div className="ResultEntryPanel-qa-actions">
              <Button variant="light" onClick={cancelAmend}>
                {t("cancel")}
              </Button>
              <Button
                variant="primary"
                disabled={
                  !amendDecision ||
                  (amendDecision === QA_DECISION.APPROVE && limsResults.effectiveOverall(amending) === "FAIL")
                }
                onClick={() => setShowAmendSign(true)}
              >
                {t("limsAmendContinue")}
              </Button>
            </div>
          </div>
        </Popup>
      )}

      {/* K13 (a) AMEND — step 2: the mandatory-reason QA e-signature (requireReason on; the server
          appends a decisionHistory entry and keeps the original signature + audit). */}
      {amending && showAmendSign && (
        <ESignatureModal
          title={t("limsAmendTitle")}
          requiredCapability="qa"
          requireReason
          excludedMemberIds={excludedMemberIds}
          commentContext="return-for-correction"
          onSign={handleAmendSign}
          onCancel={cancelAmend}
        />
      )}
    </div>
  )
}

// K13 (a): the per-sample QA decision CHAIN — the ordered list of superseded decisions kept on the
// record's `decisionHistory` (oldest first, then the current badge sits above it). Renders as a
// compact stacked list of small badges with the signer initials + day, so the correction trail —
// e.g. "Approved (CB, 12/06) → Stopped (DK, 13/06)" — is visible in one place. Nothing renders when
// the decision has never been amended (no history).
function DecisionChain({ history, memberInitials, t }) {
  if (!Array.isArray(history) || history.length === 0) return null
  return (
    <div className="ResultEntryPanel-qa-chain">
      <span className="ResultEntryPanel-qa-chain-label">{t("limsAmendChainLabel")}</span>
      <span className="ResultEntryPanel-qa-chain-list">
        {history.map((h, i) => {
          const initials = memberInitials(h.decidedBy) || "—"
          const day = h.decidedAt ? DateTime.fromISO(h.decidedAt).toFormat("dd/MM") : ""
          const caption = day ? `${initials}, ${day}` : initials
          return (
            <span key={h.signatureId || `${h.state}-${i}`} className="ResultEntryPanel-qa-chain-item">
              {i > 0 && <span className="ResultEntryPanel-qa-chain-arrow">→</span>}
              <StatusBadge variant={decidedVariant(h.state)} title={caption}>
                {t(decidedLabelKey(h.state))} ({caption})
              </StatusBadge>
            </span>
          )
        })}
      </span>
    </div>
  )
}

// The single chosen value from a Select onChange payload (the Select yields an array for multi
// and a scalar otherwise).
function firstOf(v) {
  return Array.isArray(v) ? v[0] : v
}

// PASS / FAIL / "—" badge for the EFFECTIVE judgement (okOverride already folded in). Uses the
// shared StatusBadge so the QA table renders the SAME badge family as the per-sample status chain.
function JudgementBadge({ effective, t }) {
  if (effective === null || effective === undefined) {
    return <StatusBadge variant="pending">—</StatusBadge>
  }
  const pass = effective === "PASS"
  return <StatusBadge variant={pass ? "pass" : "fail"}>{pass ? t("limsResultPass") : t("limsResultFail")}</StatusBadge>
}

// i18n key for an already-committed QA decision (terminal result state).
function decidedLabelKey(state) {
  switch (state) {
    case QA_DECISION.RERUN:
      return "limsQaReviewedRerun"
    case QA_DECISION.STOP:
      return "limsQaReviewedStopped"
    default:
      return "limsQaReviewedApproved"
  }
}

// StatusBadge variant for a committed QA decision (terminal result state — QA_DECISION values map
// 1:1 onto the result state) — the SAME colour family the per-sample status chain uses: Approved
// green, Rerun blue, Stopped red.
function decidedVariant(state) {
  switch (state) {
    case QA_DECISION.RERUN:
      return "info"
    case QA_DECISION.STOP:
      return "fail"
    default:
      return "pass"
  }
}
